/**
 * <s-scroller> — Scroll-snap carousel controls.
 * Native horizontal scrolling (touch, trackpad, keyboard) always works; this
 * element only adds previous/next buttons, slide dots and their states.
 *
 * Required child markup:
 *   [data-track] — The horizontally scrolling list (scroll-snap container).
 *
 * Commands (buttons with commandfor="<scroller id>"):
 *   --prev / --next — Scroll one viewport of the track backwards / forwards.
 *   --goto          — Scroll to the slide index in the source's data-index.
 *
 * Optional markup:
 *   [data-prev] / [data-next] — Buttons disabled at the track's ends.
 *   [data-dot]                — Buttons given aria-current for the visible slide.
 *   autoplay="<ms>"           — Auto-advance (min 2000ms); pauses on hover, focus, touch, off screen.
 *   [data-autoplay-toggle]    — Button with command="--toggle-autoplay", data-label-pause and data-label-play.
 */
class SScroller extends HTMLElement {
  #ac;
  #track;
  #frame = 0;

  connectedCallback() {
    this.#track = this.querySelector('[data-track]');
    if (!this.#track) return;
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('command', (event) => {
      const name = event.command;
      if (name === '--prev') this.#page(-1);
      else if (name === '--next') this.#page(1);
      else if (name === '--goto') this.#goto(Number(event.source?.dataset.index ?? 0));
    }, opts);

    this.#track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#sync());
    }, { ...opts, passive: true });
    window.addEventListener('resize', () => this.#sync(), opts);
    this.#sync();
    this.#setupAutoplay(opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#timer);
  }


  /* Autoplay (opt-in via the autoplay="<ms>" attribute). Advances one slide or card,
     loops back to the start at the end, and stops while the shopper hovers, focuses,
     touches, or the carousel is off screen or the tab hidden. Never runs when the
     shopper prefers reduced motion. A [data-autoplay-toggle] button pauses and resumes. */
  #timer = 0;
  #hovered = false;
  #focused = false;
  #visible = false;
  #userPaused = false;

  get #interval() {
    const value = Number(this.getAttribute('autoplay'));
    return Number.isFinite(value) && value >= 2000 ? value : 0;
  }

  #setupAutoplay(opts) {
    if (!this.#interval) return;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    if (reduced.matches) this.#userPaused = true;
    this.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') { this.#hovered = true; this.#schedule(); } }, opts);
    this.addEventListener('pointerleave', () => { this.#hovered = false; this.#schedule(); }, opts);
    this.addEventListener('focusin', () => { this.#focused = true; this.#schedule(); }, opts);
    this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) { this.#focused = false; this.#schedule(); } }, opts);
    this.#track.addEventListener('touchstart', () => { this.#userPaused = true; this.#schedule(); }, { ...opts, passive: true });
    document.addEventListener('visibilitychange', () => this.#schedule(), opts);
    this.addEventListener('command', (event) => {
      if (event.command === '--toggle-autoplay') { this.#userPaused = !this.#userPaused; this.#schedule(); }
    }, opts);
    const io = new IntersectionObserver((entries) => {
      this.#visible = entries.some((entry) => entry.isIntersecting);
      this.#schedule();
    }, { threshold: 0.4 });
    io.observe(this);
    this.#ac.signal.addEventListener('abort', () => io.disconnect());
    this.#schedule();
  }

  #schedule() {
    clearTimeout(this.#timer);
    const playing = !this.#userPaused;
    for (const button of this.querySelectorAll('[data-autoplay-toggle]')) {
      button.setAttribute('aria-pressed', String(!playing));
      button.setAttribute('aria-label', playing ? button.dataset.labelPause : button.dataset.labelPlay);
      button.toggleAttribute('data-paused', !playing);
    }
    if (!playing || this.#hovered || this.#focused || !this.#visible || document.hidden) return;
    if (!this.hasAttribute('data-scrollable')) return;
    this.#timer = setTimeout(() => { this.#advance(); this.#schedule(); }, this.#interval);
  }

  #advance() {
    const track = this.#track;
    const position = Math.abs(track.scrollLeft);
    const max = track.scrollWidth - track.clientWidth - 2;
    if (position >= max) {
      track.scrollTo({ left: 0, behavior: 'smooth' });
      return;
    }
    const first = track.children[0];
    const second = track.children[1];
    const step = first && second ? Math.abs(second.offsetLeft - first.offsetLeft) : track.clientWidth;
    track.scrollBy({ left: step * this.#direction, behavior: 'smooth' });
  }

  get #direction() {
    return getComputedStyle(this.#track).direction === 'rtl' ? -1 : 1;
  }

  get #behavior() {
    return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  #page(step) {
    const track = this.#track;
    track.scrollBy({ left: step * this.#direction * track.clientWidth * 0.9, behavior: this.#behavior });
  }

  #goto(index) {
    const slide = this.#track.children[index];
    if (!slide) return;
    const offset = slide.offsetLeft - this.#track.offsetLeft;
    this.#track.scrollTo({ left: offset, behavior: this.#behavior });
  }

  #sync() {
    const track = this.#track;
    const position = Math.abs(track.scrollLeft);
    const max = track.scrollWidth - track.clientWidth - 2;
    const scrollable = max > 0;
    this.toggleAttribute('data-scrollable', scrollable);
    for (const button of this.querySelectorAll('[data-prev]')) button.disabled = !scrollable || position <= 2;
    for (const button of this.querySelectorAll('[data-next]')) button.disabled = !scrollable || position >= max;

    const dots = this.querySelectorAll('[data-dot]');
    if (!dots.length) return;
    const width = track.clientWidth || 1;
    const active = Math.round(position / width);
    dots.forEach((dot, index) => {
      if (index === active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('s-scroller')) customElements.define('s-scroller', SScroller);
